interface FilterPanelProps {
  services: string[];
  languages: string[];
  selectedServices: Set<string>;
  selectedLanguages: Set<string>;
  searchQuery: string;
  onServicesChange: (services: Set<string>) => void;
  onLanguagesChange: (languages: Set<string>) => void;
  onSearchChange: (query: string) => void;
}

export function FilterPanel({
  services,
  languages,
  selectedServices,
  selectedLanguages,
  searchQuery,
  onServicesChange,
  onLanguagesChange,
  onSearchChange
}: FilterPanelProps) {
  const toggleService = (service: string) => {
    const newSet = new Set(selectedServices);
    if (newSet.has(service)) {
      newSet.delete(service);
    } else {
      newSet.add(service);
    }
    onServicesChange(newSet);
  };

  const toggleLanguage = (language: string) => {
    const newSet = new Set(selectedLanguages);
    if (newSet.has(language)) {
      newSet.delete(language);
    } else {
      newSet.add(language);
    }
    onLanguagesChange(newSet);
  };

  const selectAllServices = () => onServicesChange(new Set(services));
  const clearAllServices = () => onServicesChange(new Set());
  const selectAllLanguages = () => onLanguagesChange(new Set(languages));
  const clearAllLanguages = () => onLanguagesChange(new Set());

  return (
    <div className="hud-panel" style={{ padding: 'var(--space-xl)' }}>
      <h3 style={{ margin: '0 0 var(--space-lg) 0', fontSize: 'var(--font-size-xl)', fontWeight: 600 }}>
        Filters
      </h3>

      {/* Search */}
      <div style={{ marginBottom: 'var(--space-2xl)' }}>
        <label style={{
          display: 'block',
          marginBottom: 'var(--space-sm)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.03125rem',
          fontWeight: 600
        }}>
          Search
        </label>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search files..."
          style={{
            width: '100%',
            padding: 'var(--space-sm) var(--space-md)',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: '0.25rem',
            color: 'var(--color-text-primary)',
            fontSize: 'var(--font-size-base)',
            outline: 'none'
          }}
        />
      </div>

      {/* Services */}
      <FilterSection
        title="Services"
        items={services}
        selectedItems={selectedServices}
        onToggle={toggleService}
        onSelectAll={selectAllServices}
        onClearAll={clearAllServices}
      />

      {/* Languages */}
      <FilterSection
        title="Languages"
        items={languages}
        selectedItems={selectedLanguages}
        onToggle={toggleLanguage}
        onSelectAll={selectAllLanguages}
        onClearAll={clearAllLanguages}
      />
    </div>
  );
}

function FilterSection({
  title,
  items,
  selectedItems,
  onToggle,
  onSelectAll,
  onClearAll
}: {
  title: string;
  items: string[];
  selectedItems: Set<string>;
  onToggle: (item: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
}) {
  return (
    <div style={{ marginBottom: 'var(--space-2xl)' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--space-sm)'
      }}>
        <label style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.03125rem',
          fontWeight: 600
        }}>
          {title}
        </label>
        <div style={{ fontSize: 'var(--font-size-xs)', color: '#666' }}>
          <button
            onClick={onSelectAll}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-accent)',
              cursor: 'pointer',
              padding: '0',
              marginRight: 'var(--space-sm)'
            }}
          >
            All
          </button>
          <button
            onClick={onClearAll}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              padding: '0'
            }}
          >
            None
          </button>
        </div>
      </div>

      {items.map(item => (
        <label
          key={item}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: 'var(--space-xs) 0',
            cursor: 'pointer',
            fontSize: 'var(--font-size-sm)'
          }}
        >
          <input
            type="checkbox"
            checked={selectedItems.has(item)}
            onChange={() => onToggle(item)}
            style={{
              marginRight: 'var(--space-sm)',
              cursor: 'pointer',
              accentColor: 'var(--color-accent)'
            }}
          />
          <span style={{ color: selectedItems.has(item) ? 'var(--color-text-primary)' : '#666' }}>
            {item}
          </span>
        </label>
      ))}
    </div>
  );
}
